<template>
  <div class="tw:flex tw:flex-col tw:gap-4">
    <template v-if="isCisionOne">
      <div class="tw:flex tw:items-center tw:gap-1 tw:font-bold">
        {{ getLocaleText("stream_filters_audience.title") }}

        <QIcon name="info">
          <StreemTooltip
            anchor="top middle"
            self="bottom middle"
          >
            {{ getLocaleText("stream_filters_audience.tooltip") }}
          </StreemTooltip>
        </QIcon>
      </div>
      <div>
        <Tabs
          v-model="audienceTab"
          class="tw:flex tw:font-bold"
          data-testid="tabs"
          type="toggles"
        >
          <Tab
            class="tw:flex-1"
            data-testid="include-tab"
            :label="getLocaleText('stream_filters_audience.total_tab')"
            name="total"
            new-design
            type="toggle"
          />
          <Tab
            class="tw:flex-1"
            data-testid="exclude-tab"
            :label="getLocaleText('stream_filters_audience.devices_tab')"
            name="devices"
            new-design
            type="toggle"
          />
        </Tabs>
      </div>
      <div
        v-if="audienceTab === 'devices'"
        class="tw:mb-1 tw:text-xs tw:text-denim-700"
      >
        {{ getLocaleText("stream_filters_audience.devices_description") }}
      </div>
    </template>
    <div class="tw:flex tw:flex-col tw:gap-4">
      <StreamFiltersMinMaxFilter
        v-if="audienceTab === 'total'"
        v-model="audience"
        v-model:enabled="audienceEnabled"
        data-testid="audience"
      >
        <template #heading>
          <div class="tw:flex tw:items-center tw:gap-1">
            {{
              isCisionOne
                ? getLocaleText("stream_filters_audience.audience")
                : getLocaleText("stream_filters_audience.title")
            }}
            <QIcon
              v-if="isStreem"
              name="info"
            >
              <StreemTooltip
                anchor="top middle"
                self="bottom middle"
              >
                {{ getLocaleText("stream_filters_audience.tooltip") }}
              </StreemTooltip>
            </QIcon>
          </div>
        </template>
      </StreamFiltersMinMaxFilter>
      <template v-else>
        <StreamFiltersMinMaxFilter
          v-model="desktopMonthlyVisits"
          v-model:enabled="desktopMonthlyVisitsEnabled"
          data-testid="desktop"
        >
          <template #heading>
            {{ getLocaleText("stream_filters_audience.desktop") }}
          </template>
        </StreamFiltersMinMaxFilter>
        <StreamFiltersMinMaxFilter
          v-model="mobileMonthlyVisits"
          v-model:enabled="mobileMonthlyVisitsEnabled"
          data-testid="mobile"
        >
          <template #heading>
            {{ getLocaleText("stream_filters_audience.mobile") }}
          </template>
        </StreamFiltersMinMaxFilter>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { StreemTooltip, Tab, Tabs } from "shared/components/base";
import type { StreamFiltersMinMax } from "shared/helpers/StreamFilters";
import { useGlobalStore } from "shared/stores/global";

import StreamFiltersMinMaxFilter from "./StreamFiltersMinMax.vue";

const { isCisionOne, isStreem } = storeToRefs(useGlobalStore());

const audience = defineModel<StreamFiltersMinMax>("audience", {
  required: true,
});

const audienceEnabled = ref<boolean>(
  Object.values(audience.value).some((range) => range !== null)
);

const desktopMonthlyVisits = defineModel<StreamFiltersMinMax>(
  "desktopMonthlyVisits",
  { required: true }
);

const desktopMonthlyVisitsEnabled = ref<boolean>(
  Object.values(desktopMonthlyVisits.value).some((range) => range !== null)
);

const mobileMonthlyVisits = defineModel<StreamFiltersMinMax>(
  "mobileMonthlyVisits",
  { required: true }
);

const mobileMonthlyVisitsEnabled = ref<boolean>(
  Object.values(mobileMonthlyVisits.value).some((range) => range !== null)
);

const audienceTab = ref<string>(
  desktopMonthlyVisitsEnabled.value || mobileMonthlyVisitsEnabled.value
    ? "devices"
    : "total"
);

watch(audienceTab, () => {
  audience.value = {
    min: null,
    max: null,
  };

  audienceEnabled.value = false;

  desktopMonthlyVisits.value = {
    min: null,
    max: null,
  };

  desktopMonthlyVisitsEnabled.value = false;

  mobileMonthlyVisits.value = {
    min: null,
    max: null,
  };

  mobileMonthlyVisitsEnabled.value = false;
});
</script>
